// File: src/hooks/useLoadScript.ts

import { useState, useEffect } from 'react';
import { isPrerender } from '../utils/isPrerender';

// This hook takes a script URL and loads it into the document head.
// It returns 'true' when the script has loaded successfully.
export const useLoadScript = (src: string): boolean => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Never inject during react-snap: the tag gets BAKED into the prerendered
    // HTML of every page whose component calls this hook, shipping an eager
    // third-party download sitewide (checkout.razorpay.com, found 2026-10-10).
    if (isPrerender()) return;
    // Check if a script with this src already exists to avoid duplicates
    if (document.querySelector(`script[src="${src}"]`)) {
      setLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;

    const onScriptLoad = () => {
      setLoaded(true);
    };

    const onScriptError = () => {
      console.error(`Error loading script: ${src}`);
      setLoaded(false);
    };

    script.addEventListener('load', onScriptLoad);
    script.addEventListener('error', onScriptError);

    document.body.appendChild(script);

    // Clean up the event listeners when the component unmounts
    return () => {
      script.removeEventListener('load', onScriptLoad);
      script.removeEventListener('error', onScriptError);
    };
  }, [src]); // Only re-run if the src changes

  return loaded;
};